import React from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

export default function Show({
  facility,
  stats = {},
  pending_invitations = [],
  current_user_role,
}) {
  const isOwnerOrAdmin = current_user_role === 'owner' || current_user_role === 'admin';
  const isOwner = current_user_role === 'owner';

  const [resentId, setResentId] = React.useState(null);

  const handleResendInvitation = (invitationId) => {
    router.post(`/facilities/${facility.id}/invitations/${invitationId}/resend`, {}, {
      onSuccess: () => {
        setResentId(invitationId);
        setTimeout(() => setResentId(null), 3000);
      },
    });
  };

  const handleRevokeInvitation = (invitationId) => {
    if (!confirm('Revoke this invitation?')) return;
    router.post(`/facilities/${facility.id}/invitations/${invitationId}/revoke`);
  };

  const seatsPurchased = stats.seats_purchased || 0;
  const seatsAssigned = stats.seats_assigned || 0;
  const seatsAvailable = stats.seats_available || 0;
  const seatUsagePct = seatsPurchased > 0 ? Math.round((seatsAssigned / seatsPurchased) * 100) : 0;

  const quickActions = [
    {
      label: 'Team Members',
      description: 'View and manage caregivers and admins.',
      href: '/team',
      show: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
    },
    {
      label: 'Assign Training',
      description: 'Give team members seats from your available licenses.',
      href: '/assign-training',
      show: isOwnerOrAdmin,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    },
    {
      label: 'Billing',
      description: 'Purchase seats, manage subscriptions, view invoices.',
      href: `/facilities/${facility.id}/billing`,
      show: isOwner,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
      ),
    },
    {
      label: 'Facility Settings',
      description: 'Update facility name, type, states, and license.',
      href: `/facilities/${facility.id}/edit`,
      show: isOwnerOrAdmin,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ].filter((a) => a.show);

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        {/* Header */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 sm:p-8 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                {facility.facility_type_code && (
                  <span className="inline-flex items-center rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 border border-indigo-200">
                    {facility.facility_type_code}
                  </span>
                )}
                {(facility.states_list || []).map((state) => (
                  <span key={state} className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                    {state}
                  </span>
                ))}
                {facility.license_number && (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                    License #{facility.license_number}
                  </span>
                )}
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-gray-900">{facility.name}</h1>
              <p className="mt-1 text-sm text-gray-500">{facility.facility_type_display}</p>
            </div>
            {isOwnerOrAdmin && (
              <Link
                href={`/facilities/${facility.id}/edit`}
                className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit facility
              </Link>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatTile
            label="Team Members"
            value={stats.team_count ?? 0}
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            }
          />
          <StatTile
            label="Training Seats"
            value={`${seatsAssigned}/${seatsPurchased}`}
            sublabel={seatsPurchased > 0 ? `${seatsAvailable} available` : 'No seats purchased'}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-600"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
            progress={seatsPurchased > 0 ? seatUsagePct : null}
          />
          <StatTile
            label="Active Training"
            value={stats.active_training ?? 0}
            sublabel="In progress"
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            }
          />
          <StatTile
            label="Pending Invites"
            value={stats.pending_count ?? 0}
            sublabel={(stats.pending_count ?? 0) === 0 ? 'No pending' : 'Awaiting response'}
            iconBg="bg-rose-50"
            iconColor="text-rose-600"
            icon={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            }
          />
        </div>

        {/* Quick actions */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">Manage this facility</h2>
          <p className="text-sm text-gray-500 mb-5">Jump straight into the area you need.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {quickActions.map((action) => (
              <Link
                key={action.label}
                href={action.href}
                className="group flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50/40 transition"
              >
                <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center shrink-0 group-hover:bg-indigo-100">
                  {action.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900">{action.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{action.description}</p>
                </div>
                <svg className="w-5 h-5 text-gray-300 group-hover:text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            ))}
          </div>
        </div>

        {/* Pending invitations */}
        {isOwnerOrAdmin && pending_invitations.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-8 h-8 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center shrink-0">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg font-semibold text-gray-900">Pending invitations</h3>
                <p className="text-sm text-gray-500 mt-0.5">Waiting for these users to accept and sign in.</p>
              </div>
              <Link
                href={`/facilities/${facility.id}/invite_user`}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-700 shadow-sm transition"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                </svg>
                Invite
              </Link>
            </div>

            <div className="divide-y divide-gray-100">
              {pending_invitations.map((inv) => (
                <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <div className="shrink-0 w-9 h-9 bg-gradient-to-br from-amber-400 to-rose-500 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                    {(inv.name || inv.email)?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900">{inv.name || inv.email}</p>
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 capitalize">
                        {inv.role}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{inv.email} · Sent {inv.created_at}</p>
                  </div>
                  <div className="flex gap-2 sm:ml-auto">
                    <button
                      onClick={() => handleResendInvitation(inv.id)}
                      className="inline-flex items-center rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 cursor-pointer transition"
                    >
                      {resentId === inv.id ? 'Sent!' : 'Resend'}
                    </button>
                    <button
                      onClick={() => handleRevokeInvitation(inv.id)}
                      className="inline-flex items-center rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-xs font-semibold text-rose-700 hover:bg-rose-50 cursor-pointer transition"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function StatTile({ label, value, sublabel, icon, iconBg, iconColor, progress }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
          <p className="mt-1.5 text-2xl font-bold text-gray-900">{value}</p>
          {sublabel && <p className="mt-0.5 text-xs text-gray-500">{sublabel}</p>}
        </div>
        <div className={`w-9 h-9 ${iconBg} ${iconColor} rounded-lg flex items-center justify-center shrink-0`}>
          {icon}
        </div>
      </div>
      {progress != null && (
        <div className="mt-3">
          <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all"
              style={{ width: `${Math.min(100, progress)}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-400">{progress}% assigned</p>
        </div>
      )}
    </div>
  );
}
